import { useWaiterRouter } from '@segments/Routing/client';
import * as React from 'react';
import { useAuthCtx } from '~logic/auth/AuthProvider';
import { getOrderFromUrl } from '~logic/helpers/getOrderFromUrl';
import { getGatewayUrls, usePaymentStatus } from '~shared/segments/FormScreen';
/**
 * Screen-route validation based on the payment status of the order.
 * If the screen route is not in line with the status of the payment,
 * the customer is redirected to the correct screen to continue.
 *
 * It is used:
 * - on multiple screens wrapped by the `ValidateOrderStatus` cmp as a generic validation
 * - within the `ValidatePaymentScreen` to directly validate the current payment status
 * - within the `PaymentLockedScreen` to validate screen route
 * AND get `gatewayUrls` of the payment in process (if there is any)
 *
 * For the latter use, this hook returns `isFetching` and `isPrePaid` booleans
 * that are used to render the corresponding UI.
 */

export var useValidateOrderStatus = function useValidateOrderStatus() {
  var router = useWaiterRouter(); // the orderId & userToken values from url OR both null if these are not present in the url

  var orderAndUser = getOrderFromUrl();

  var _useAuthCtx = useAuthCtx(),
      authToken = _useAuthCtx.authToken;

  var _usePaymentStatus = usePaymentStatus(orderAndUser),
      paymentStatus = _usePaymentStatus.paymentStatus,
      orderId = _usePaymentStatus.orderId,
      isFetching = _usePaymentStatus.isFetching;

  React.useEffect(function () {
    if (!paymentStatus || isFetching) {
      return;
    }

    var screenRoute = router.screenRoute;
    var urlParams = {
      orderId: orderId,
      userToken: authToken
    };
    var goToScreen = createGoToScreen(router); // current screen route is one of these where the customer can still modify the order

    var isOnWizardScreen = screenRoute === 'home' || //
    screenRoute === 'menu' || screenRoute === 'form';
    var isOnValidationScreen = screenRoute === 'validate-payment'; // current screen route is one of the "locked order" screens
    // where order is locked for changes

    var isOnLockedOrderScreen = screenRoute === 'order-payment-locked' || //
    screenRoute === 'order-locked';
    var isInPaymentGate = paymentStatus.isInPaymentGate,
        isPrePaid = paymentStatus.isPrePaid,
        order = paymentStatus.order;
    var acceptedAt = order.acceptedAt,
        isLocked = order.isLocked,
        orderStates = order.orderStates;
    var isPlaced = Boolean(acceptedAt);
    var isWifiDelayed = orderStates === null || orderStates === void 0 ? void 0 : orderStates.some(function (state) {
      return state["enum"] === 'WIFI_DELAYED';
    });

    if (isPrePaid) {
      // should not be on home/menu/form/validate-payment
      if (isOnWizardScreen || isOnValidationScreen) {
        // redirect to the SubmittedScreen
        goToScreen('order-placed', urlParams);
      }

      return;
    } // wait until order is resolved before redirecting


    if (isWifiDelayed) {
      return;
    }

    if (isPlaced) {
      // should not be on home/menu/form/validate-payment
      if (isOnWizardScreen || isOnValidationScreen || isOnLockedOrderScreen) {
        // redirect to the SubmittedScreen
        goToScreen('order-placed', urlParams);
      }

      return;
    }

    if (!isLocked) {
      // it's ok to be on home, menu, form (editable order)
      if (!isOnWizardScreen) {
        goToScreen('home');
      }

      return;
    }

    if (isInPaymentGate) {
      // we have some pending payment process bound to this order
      // if not already there, should go to PaymentLockedScreen
      if (screenRoute === 'order-payment-locked') {
        return;
      }

      goToScreen('order-payment-locked', urlParams);
      return;
    } // the order is locked – can't be edited
    // and we have no payment process bound to it
    // if not already there, should go to OrderLockedScreen


    if (screenRoute === 'order-locked') {
      return;
    }

    goToScreen('order-locked', urlParams);
  }, [authToken, isFetching, orderId, paymentStatus, router]);
  return {
    isFetching: isFetching,
    isPrePaid: Boolean(paymentStatus === null || paymentStatus === void 0 ? void 0 : paymentStatus.isPrePaid),
    gatewayUrls: getGatewayUrls(paymentStatus)
  };
};

/**
 * returns `goToScreen` function, that:
 * - calls the `router.replace`
 * - handles adding of the `orderId` & `userToken` to the url
 */
var createGoToScreen = function createGoToScreen(router) {
  return function (screenRoute, urlParams) {
    if (!urlParams) {
      router.replace(screenRoute);
      return;
    }

    var orderId = urlParams.orderId,
        userToken = urlParams.userToken;

    if (!orderId || !userToken) {
      return;
    }

    router.replace(screenRoute, {
      urlParams: {
        orderId: orderId,
        userToken: btoa(userToken)
      }
    });
  };
};